import isEqual from 'lodash.isequal';
import { CurveBeamLogo } from '../components/CurveBeamLogo';

declare global {
  interface Window {
    config: any;
  }
}

/**
 * [CurveBeamAI] Configuration service - loads viewer config from backend API (AWS AppConfig).
 * All properties are optional since AppConfig may not provide all values,
 * and we use undefined checks when updating the environment object.
 */
/**
 * [WTD-1826] AppSync Events endpoints for this environment. Host only - no scheme, no path.
 *
 * The two are kept separate rather than one derived from the other, because under a custom domain
 * they collapse to the SAME host with different paths, which any string derivation would get wrong.
 */
export interface AppSyncConfig {
  /** HTTP endpoint host - publishing, and the `host` in the connection authorization object. */
  httpDomain: string;
  /** Realtime endpoint host - where the socket is opened. */
  realtimeDomain: string;
}

export interface EnvironmentConfig {
  validateAccessTokenEndpoint?: string;
  /**
   * [WTD-1826] AppSync Events endpoints. Its PRESENCE is the switch, so it cannot be
   * half-configured: one endpoint without the other would publish happily and never subscribe.
   *
   * Replaces `clientId` and `webSocketURL`, neither of which is read any more. The Cognito app
   * client id was only ever placed in the old socket's connect query string and no Lambda
   * validated it, and the socket URL belongs to a transport this application no longer speaks.
   */
  appSync?: AppSyncConfig;
  backendBaseUrl?: string;
  awsHealthImagingEndpoint?: string;
  dmpHomeURL?: string;
  referrerWhitelist?: string | string[];
  version?: string;
  region?: string;
  awsRegion?: string;
  pollingIntervalMinutes?: number;
}

/**
 * [CurveBeam] - Runtime environment object populated from AppConfig.
 * This object is initialized during app startup and can be updated at runtime
 * when new configuration versions are deployed.
 *
 * Components can import and use this directly:
 * ```typescript
 * import { environment } from './CurveBeamAI/services/config.service';
 * const apiUrl = environment.backendBaseUrl;
 * ```
 */
export let environment: {
  validateAccessTokenEndpoint: string;
  appSync?: AppSyncConfig;
  backendBaseUrl: string;
  awsHealthImagingEndpoint: string;
  dmpHomeURL: string;
  referrerWhitelist: string | string[];
  version: string;
  region: string;
  awsRegion: string;
  pollingIntervalMinutes?: number;
} = {
  // Bootstrap values - will be overridden by AppConfig
  validateAccessTokenEndpoint: '',
  backendBaseUrl: '',
  awsHealthImagingEndpoint: '',
  dmpHomeURL: '',
  referrerWhitelist: '',
  version: '',
  region: '',
  awsRegion: '',
};

/**
 * [WTD-1826] Whether the AppSync endpoint pair is actually usable.
 *
 * Asked about the two endpoints rather than about the object holding them, which is what the first
 * version did. `!!environment.appSync` is a truthiness test on a container, and every object is
 * truthy in JavaScript - `{}` included - so a profile carrying `appSync: {}`, or one endpoint
 * without the other, selected the session-channel flow and reached the library with `undefined`
 * hosts. Not empty strings: reading a missing property yields `undefined`, which gets interpolated
 * into the endpoint URL, so the symptom is a request to a host literally named `undefined` and a
 * DNS failure that names no configuration key at all.
 *
 * TypeScript does not cover this, although AppSyncConfig declares both fields required. The value
 * arrives as JSON from /config/get and is admitted by `responseData.data as EnvironmentConfig` - a
 * type assertion, which is a compile-time claim that emits no runtime check. Whatever the AppConfig
 * profile happens to contain is what ends up here.
 *
 * Deliberately NOT used as the flow switch in useCurveBeamAppInit, which tests only whether the
 * object is present. The two questions differ on purpose: an absent appSync is an environment that
 * has not been migrated and should fall back, while a present-but-partial one is a broken profile
 * that should produce an error page naming the key. Testing the endpoints at the switch would
 * collapse the second case into the first and boot the viewer with no session validation at all.
 */
export function hasAppSyncConfig(): boolean {
  return !!(environment?.appSync?.httpDomain && environment?.appSync?.realtimeDomain);
}

export class ConfigService {
  private configLoaded = false;
  private configLoadPromise: Promise<void> | null = null;
  private pollingIntervalMs: number = 2 * 60 * 1000;
  private pollingTimer: ReturnType<typeof setInterval> | null = null;
  private appConfigServiceParams: { applicationId: string; profileName?: string } | null = null;
  public lastError: { message: string; details: any } | null = null;

  constructor() {}

  resetConfig(): void {
    this.configLoaded = false;
    this.lastError = null;
    this.configLoadPromise = null;
    try {
      sessionStorage.removeItem('config-load-error');
    } catch (e) {}
  }

  private storeError(errorMessage: string, errorDetails: any): void {
    this.lastError = { message: errorMessage, details: errorDetails };
    try {
      sessionStorage.setItem('config-load-error', JSON.stringify(this.lastError));
    } catch (e) {
      console.error('[ConfigService] Could not store error in sessionStorage:', e);
    }
  }

  async loadConfig(startPolling: boolean = false): Promise<void> {
    if (this.configLoaded) return;
    if (this.configLoadPromise) return this.configLoadPromise;
    this.configLoadPromise = this.performConfigLoad(startPolling);
    try {
      await this.configLoadPromise;
    } finally {
      this.configLoadPromise = null;
    }
  }

  private async performConfigLoad(startPolling: boolean = false): Promise<void> {
    const CONFIG_LOAD_TIMEOUT_MS = 30000;
    let configUrl = '';

    try {
      const apiEndpoint = (window as any).config?.apiEndpoint || '';
      const applicationId = (window as any).config?.appConfigService?.applicationId;

      if (!applicationId) {
        throw new Error(
          'applicationId is required in appConfigService configuration. ' +
            'Please ensure the APP_CONFIG file contains appConfigService.applicationId.'
        );
      }

      configUrl = `${apiEndpoint}/config/get?applicationId=${encodeURIComponent(applicationId)}`;
      const profileName = (window as any).config?.appConfigService?.profileName;
      if (profileName) {
        configUrl += `&profileName=${encodeURIComponent(profileName)}`;
      }

      this.appConfigServiceParams = { applicationId, profileName };
      console.log('[ConfigService] Loading config from:', configUrl);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CONFIG_LOAD_TIMEOUT_MS);

      let response: Response;
      try {
        response = await fetch(configUrl, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
          credentials: 'omit' as RequestCredentials,
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (controller.signal.aborted) {
          this.storeError(
            'Configuration could not be loaded: Request timeout. The backend did not respond within 30 seconds.',
            {
              errorType: 'Timeout',
              requestUrl: configUrl,
              timeoutMs: CONFIG_LOAD_TIMEOUT_MS,
              frontendOrigin: window.location.origin,
              frontendProtocol: window.location.protocol,
              backendProtocol: new URL(configUrl).protocol,
              timestamp: new Date().toISOString(),
            }
          );
          throw new Error('Configuration could not be loaded: Request timeout.');
        }
        throw fetchError;
      }

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage =
          'Configuration could not be loaded from backend. Please ensure the backend service is running and accessible.';
        const errorDetails: any = {
          requestUrl: configUrl,
          frontendOrigin: window.location.origin,
          frontendProtocol: window.location.protocol,
          backendProtocol: new URL(configUrl).protocol,
          errorStatus: response.status,
          errorStatusText:
            response.statusText && response.statusText !== 'OK' ? response.statusText : undefined,
          timestamp: new Date().toISOString(),
        };

        if (response.status === 0) {
          const isMixedContent =
            window.location.protocol === 'http:' && configUrl.startsWith('https:');
          if (isMixedContent) {
            errorMessage =
              'Configuration could not be loaded: Mixed Content Error. The page is served over HTTP but trying to access HTTPS API.';
            errorDetails.errorType = 'Mixed Content';
          } else {
            errorMessage =
              'Configuration could not be loaded: Network error (likely CORS preflight failure).';
            errorDetails.errorType = 'Network Error (Likely CORS)';
          }
        } else if (response.status >= 400) {
          errorMessage = `Configuration could not be loaded: Backend returned status ${response.status}`;
          errorDetails.errorType = 'HTTP Error';
          errorDetails.httpStatus = response.status;
        }

        this.storeError(errorMessage, errorDetails);
        throw new Error(errorMessage);
      }

      const responseData = await response.json();

      if (responseData.code && responseData.code >= 400) {
        const errorMessage = responseData.message || 'Unknown error';
        this.storeError(errorMessage, {
          errorType: 'Backend Error',
          backendMessage: errorMessage,
          backendCode: responseData.code,
          requestUrl: configUrl,
          frontendOrigin: window.location.origin,
          frontendProtocol: window.location.protocol,
          backendProtocol: new URL(configUrl).protocol,
          errorStatus: responseData.code,
          timestamp: new Date().toISOString(),
        });
        throw new Error(errorMessage);
      }

      if (!responseData || !responseData.data) {
        this.storeError('Backend returned empty configuration data.', {
          errorType: 'Empty Response',
          requestUrl: configUrl,
          frontendOrigin: window.location.origin,
          frontendProtocol: new URL(configUrl).protocol,
          timestamp: new Date().toISOString(),
        });
        throw new Error('Backend returned empty configuration data.');
      }

      this.updateEnvironmentObject(responseData.data as EnvironmentConfig);
      this.mergeWithBaseConfig();

      const pollingIntervalMinutes = (responseData.data as EnvironmentConfig)
        .pollingIntervalMinutes;
      if (pollingIntervalMinutes && pollingIntervalMinutes > 0) {
        this.pollingIntervalMs = pollingIntervalMinutes * 60 * 1000;
      }

      this.configLoaded = true;
      if (startPolling) this.startPolling();
    } catch (error: any) {
      if (this.lastError) {
        console.error(
          '[ConfigService] Critical error: Failed to load configuration from backend.',
          error
        );
        throw error;
      }

      let backendProtocol = '';
      try {
        if (configUrl) backendProtocol = new URL(configUrl).protocol;
      } catch (e) {}

      const errorDetails: any = {
        requestUrl: configUrl || 'Not available',
        frontendOrigin: window.location.origin,
        frontendProtocol: window.location.protocol,
        backendProtocol,
        timestamp: new Date().toISOString(),
      };

      let errorMessage = 'Configuration could not be loaded from backend.';

      if (error?.name === 'AbortError' || error instanceof DOMException) {
        errorMessage = 'Configuration could not be loaded: Request was aborted.';
        errorDetails.errorType = 'AbortError';
        errorDetails.timeoutMs = CONFIG_LOAD_TIMEOUT_MS;
      } else if (error instanceof SyntaxError || error?.name === 'SyntaxError') {
        errorMessage = 'Configuration could not be loaded: Invalid response format.';
        errorDetails.errorType = 'JSON Parse Error';
        errorDetails.parseError = error.message;
      } else if (error instanceof TypeError || error?.name === 'TypeError') {
        errorMessage = 'Configuration could not be loaded: Network or configuration error.';
        errorDetails.errorType = 'TypeError';
        errorDetails.typeError = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
        errorDetails.errorType = 'Unknown Error';
        errorDetails.errorName = error.name;
        errorDetails.errorMessage = error.message;
      } else {
        errorMessage =
          typeof error === 'string'
            ? error
            : 'Configuration could not be loaded: An unexpected error occurred.';
        errorDetails.errorType = 'Unexpected Error';
        errorDetails.errorValue = String(error);
      }

      this.storeError(errorMessage, errorDetails);
      console.error('[ConfigService] Critical error: Failed to load configuration from backend.', {
        error,
        errorType: errorDetails.errorType,
        errorName: error?.name,
        errorMessage: error?.message,
      });
      throw new Error(errorMessage);
    }
  }

  private updateEnvironmentObject(config: EnvironmentConfig): void {
    if (config.validateAccessTokenEndpoint !== undefined)
      environment.validateAccessTokenEndpoint = config.validateAccessTokenEndpoint;
    // [WTD-1826] Assigned wholesale rather than merged - a partially-updated endpoint pair would be
    // worse than either the old or the new one, since one endpoint without the other publishes
    // happily and never subscribes. clientId and webSocketURL went with the old transport.
    if (config.appSync !== undefined) environment.appSync = config.appSync;
    if (config.backendBaseUrl !== undefined) environment.backendBaseUrl = config.backendBaseUrl;
    if (config.awsHealthImagingEndpoint !== undefined)
      environment.awsHealthImagingEndpoint = config.awsHealthImagingEndpoint;
    if (config.dmpHomeURL !== undefined) environment.dmpHomeURL = config.dmpHomeURL;
    if (config.referrerWhitelist !== undefined) {
      environment.referrerWhitelist = config.referrerWhitelist;
    }
    if (config.version !== undefined) environment.version = config.version;
    if (config.region !== undefined) environment.region = config.region;
    if (config.awsRegion !== undefined) environment.awsRegion = config.awsRegion;
    if (config.pollingIntervalMinutes !== undefined)
      environment.pollingIntervalMinutes = config.pollingIntervalMinutes;

    if (config.pollingIntervalMinutes && config.pollingIntervalMinutes > 0) {
      const newPollingIntervalMs = config.pollingIntervalMinutes * 60 * 1000;
      if (this.pollingIntervalMs !== newPollingIntervalMs) {
        this.pollingIntervalMs = newPollingIntervalMs;
        if (this.pollingTimer) {
          this.stopPolling();
          this.startPolling();
        }
      }
    }
  }

  private mergeWithBaseConfig(): void {
    try {
      if (typeof window !== 'undefined' && window.config) {
        const finalConfig = {
          ...window.config,
          validateAccessTokenEndpoint: environment.validateAccessTokenEndpoint,
          appSync: environment.appSync,
          backendBaseUrl: environment.backendBaseUrl,
          awsHealthImagingEndpoint: environment.awsHealthImagingEndpoint,
          dmpHomeURL: environment.dmpHomeURL,
          referrerWhitelist: Array.isArray(environment.referrerWhitelist)
            ? environment.referrerWhitelist
            : environment.referrerWhitelist
              ? [environment.referrerWhitelist]
              : [],
          version: environment.version,
          region: environment.region,
          awsRegion: environment.awsRegion,
          // [CurveBeamAI] CurveBeam logo in header - replaces OHIF branding
          whiteLabeling: {
            ...((window.config as any)?.whiteLabeling ?? {}),
            createLogoComponentFn: (ReactLib: any) => ReactLib.createElement(CurveBeamLogo),
          },
        };

        // [CurveBeamAI] Build HealthImaging base URL: awsRegion from API, datastore from URL params
        const awsRegion = environment.awsRegion || '';
        const datastore =
          typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('datastore') || ''
            : '';
        let baseUrl = environment.awsHealthImagingEndpoint;
        if (awsRegion && datastore) {
          baseUrl = `https://dicom-medical-imaging.${awsRegion}.amazonaws.com/datastore/${datastore}`;
        }
        if (baseUrl && finalConfig.dataSources && Array.isArray(finalConfig.dataSources)) {
          finalConfig.dataSources = finalConfig.dataSources.map((ds: any) => {
            const cfg = ds.configuration;
            const needsInjection =
              (ds.sourceName === 'aws-healthimaging' ||
                ds.configuration?.name === 'aws-healthimaging') &&
              cfg &&
              (!cfg.wadoRoot || cfg.wadoRoot === '');
            if (needsInjection) {
              return {
                ...ds,
                configuration: {
                  ...cfg,
                  wadoUriRoot: baseUrl,
                  qidoRoot: baseUrl,
                  wadoRoot: baseUrl,
                },
              };
            }
            return ds;
          });
        }

        window.config = finalConfig;
      }
    } catch (error) {
      console.warn('[ConfigService] Failed to merge with base config:', error);
    }
  }

  startPolling(): void {
    if (this.pollingTimer) clearInterval(this.pollingTimer);
    if (!this.configLoaded) {
      console.warn('[ConfigService] Cannot start polling - config not loaded yet');
      return;
    }
    this.pollingTimer = setInterval(async () => {
      try {
        await this.checkForUpdates();
      } catch (error) {
        console.warn('[ConfigService] Config update check failed:', error);
      }
    }, this.pollingIntervalMs);
  }

  private async checkForUpdates(): Promise<void> {
    if (!this.configLoaded) return;

    try {
      const apiEndpoint = (window as any).config?.apiEndpoint || environment.backendBaseUrl || '';
      if (!apiEndpoint) return;

      if (!this.appConfigServiceParams) {
        const applicationId = (window as any).config?.appConfigService?.applicationId;
        if (!applicationId) return;
        this.appConfigServiceParams = {
          applicationId,
          profileName: (window as any).config?.appConfigService?.profileName,
        };
      }

      let configUrl = `${apiEndpoint}/config/get?applicationId=${encodeURIComponent(this.appConfigServiceParams.applicationId)}`;
      if (this.appConfigServiceParams.profileName) {
        configUrl += `&profileName=${encodeURIComponent(this.appConfigServiceParams.profileName)}`;
      }

      const response = await fetch(configUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'omit' as RequestCredentials,
      });

      if (!response.ok) return;

      const responseData = await response.json();
      if (!responseData || !responseData.data) return;

      const newConfig = responseData.data as EnvironmentConfig;
      const currentVersion = environment.version;
      const newVersion = newConfig.version;

      const currentConfigForCompare: any = { ...environment };
      const newConfigForCompare: any = { ...newConfig };
      delete currentConfigForCompare.pollingIntervalMinutes;
      delete newConfigForCompare.pollingIntervalMinutes;

      const hasChanged =
        currentVersion !== newVersion || !isEqual(currentConfigForCompare, newConfigForCompare);

      if (hasChanged) {
        this.updateEnvironmentObject(newConfig);
      }
    } catch (error) {
      console.warn('[ConfigService] Error during config update check:', error);
    }
  }

  stopPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  cleanup(): void {
    this.stopPolling();
  }
}

export const configService = new ConfigService();
